/* eslint-disable no-unused-vars */
// =============================================================================
// CENTRALIZED ENUMS AND STATIC LISTS FOR HEALTHCARE EMR
// =============================================================================
// This file serves as the single source of truth for all static lists, enums,
// and constants used throughout the application. All components should import
// from this file instead of defining their own constants.

// =============================================================================
// DEPARTMENT NAMES
// =============================================================================

/**
 * Department Names - Standard healthcare department classifications
 * Used for categorizing and standardizing department names across facilities
 */
export enum DepartmentName {
  EMERGENCY_DEPARTMENT = 'Emergency Department (ED)',
  CARDIOLOGY = 'Cardiology',
  RADIOLOGY_IMAGING = 'Radiology / Imaging',
  INTERNAL_MEDICINE = 'Internal Medicine',
  PEDIATRICS = 'Pediatrics',
  SURGERY_OR = 'Surgery (OR)',
  INTENSIVE_CARE_UNIT = 'Intensive Care Unit (ICU)',
  NEONATAL_ICU = 'Neonatal ICU (NICU)',
  LABOR_DELIVERY = 'Labor & Delivery (L&D)',
  ORTHOPEDICS = 'Orthopedics',
  GASTROENTEROLOGY = 'Gastroenterology (GI)',
  NEUROLOGY = 'Neurology',
  ONCOLOGY = 'Oncology',
  DERMATOLOGY = 'Dermatology',
  UROLOGY = 'Urology',
  ENT = 'ENT (Otolaryngology)',
  OBSTETRICS_GYNECOLOGY = 'Obstetrics & Gynecology (OB/GYN)',
  PHYSICAL_THERAPY = 'Physical Therapy (PT)',
  OCCUPATIONAL_THERAPY = 'Occupational Therapy (OT)',
  SPEECH_THERAPY = 'Speech Therapy',
  BEHAVIORAL_HEALTH = 'Behavioral Health / Psychiatry',
  NUTRITION_DIETETICS = 'Nutrition / Dietetics',
  SOCIAL_WORK = 'Social Work / Case Management',
  ADMINISTRATION_BILLING = 'Administration / Billing',
  PHARMACY = 'Pharmacy',
  LABORATORY = 'Laboratory (Clinical Pathology)',
  ANESTHESIOLOGY = 'Anesthesiology',
  PULMONOLOGY_SLEEP = 'Pulmonology / Sleep Lab',
  INFECTIOUS_DISEASE = 'Infectious Disease',
  ENDOCRINOLOGY = 'Endocrinology',
  NEPHROLOGY_DIALYSIS = 'Nephrology / Dialysis',
  GERIATRICS = 'Geriatrics',
  REHABILITATION_MEDICINE = 'Rehabilitation Medicine',
  PAIN_MANAGEMENT = 'Pain Management',
  SLEEP_MEDICINE = 'Sleep Medicine',
  FAMILY_MEDICINE = 'Family Medicine',
}

/**
 * Array of all department names for UI dropdowns
 */
export const DEPARTMENT_NAMES = Object.values(DepartmentName);

/**
 * Department name descriptions for UI display
 */
export const DEPARTMENT_NAME_DESCRIPTIONS: Record<DepartmentName, string> = {
  [DepartmentName.EMERGENCY_DEPARTMENT]: 'Emergency medical care and treatment for acute conditions and injuries',
  [DepartmentName.CARDIOLOGY]: 'Diagnosis and treatment of heart and cardiovascular conditions',
  [DepartmentName.RADIOLOGY_IMAGING]: 'Diagnostic imaging services including X-rays, CT scans, and MRIs',
  [DepartmentName.INTERNAL_MEDICINE]: 'Comprehensive adult medical care and disease management',
  [DepartmentName.PEDIATRICS]: 'Medical care for infants, children, and adolescents',
  [DepartmentName.SURGERY_OR]: 'Surgical procedures and pre/post-operative care',
  [DepartmentName.INTENSIVE_CARE_UNIT]: 'Critical care for patients with severe or life-threatening conditions',
  [DepartmentName.NEONATAL_ICU]: 'Specialized care for premature and critically ill newborns',
  [DepartmentName.LABOR_DELIVERY]: 'Childbirth services and maternal care',
  [DepartmentName.ORTHOPEDICS]: 'Treatment of musculoskeletal conditions and injuries',
  [DepartmentName.GASTROENTEROLOGY]: 'Digestive system disorders and procedures',
  [DepartmentName.NEUROLOGY]: 'Nervous system disorders and neurological conditions',
  [DepartmentName.ONCOLOGY]: 'Cancer diagnosis, treatment, and management',
  [DepartmentName.DERMATOLOGY]: 'Skin conditions and dermatological procedures',
  [DepartmentName.UROLOGY]: 'Urinary system and male reproductive health',
  [DepartmentName.ENT]: 'Ear, nose, and throat conditions',
  [DepartmentName.OBSTETRICS_GYNECOLOGY]: 'Women\'s health, pregnancy, and reproductive care',
  [DepartmentName.PHYSICAL_THERAPY]: 'Physical rehabilitation and mobility therapy',
  [DepartmentName.OCCUPATIONAL_THERAPY]: 'Daily living skills and functional therapy',
  [DepartmentName.SPEECH_THERAPY]: 'Communication and swallowing disorders',
  [DepartmentName.BEHAVIORAL_HEALTH]: 'Mental health and psychiatric care',
  [DepartmentName.NUTRITION_DIETETICS]: 'Nutritional assessment and dietary planning',
  [DepartmentName.SOCIAL_WORK]: 'Patient advocacy and case management services',
  [DepartmentName.ADMINISTRATION_BILLING]: 'Administrative and billing operations',
  [DepartmentName.PHARMACY]: 'Medication dispensing and pharmaceutical care',
  [DepartmentName.LABORATORY]: 'Medical laboratory testing and analysis',
  [DepartmentName.ANESTHESIOLOGY]: 'Anesthesia services and pain management',
  [DepartmentName.PULMONOLOGY_SLEEP]: 'Respiratory conditions and sleep disorders',
  [DepartmentName.INFECTIOUS_DISEASE]: 'Infectious disease diagnosis and treatment',
  [DepartmentName.ENDOCRINOLOGY]: 'Hormone and metabolic disorders',
  [DepartmentName.NEPHROLOGY_DIALYSIS]: 'Kidney disease and dialysis services',
  [DepartmentName.GERIATRICS]: 'Specialized care for elderly patients',
  [DepartmentName.REHABILITATION_MEDICINE]: 'Comprehensive rehabilitation services',
  [DepartmentName.PAIN_MANAGEMENT]: 'Chronic pain treatment and management',
  [DepartmentName.SLEEP_MEDICINE]: 'Sleep disorder diagnosis and treatment',
  [DepartmentName.FAMILY_MEDICINE]: 'Comprehensive primary care for all ages',
};

/**
 * Department codes mapping for auto-generation
 */
export const DEPARTMENT_CODES: Record<DepartmentName, string> = {
  [DepartmentName.EMERGENCY_DEPARTMENT]: 'ED',
  [DepartmentName.CARDIOLOGY]: 'CARD',
  [DepartmentName.RADIOLOGY_IMAGING]: 'RAD',
  [DepartmentName.INTERNAL_MEDICINE]: 'IM',
  [DepartmentName.PEDIATRICS]: 'PED',
  [DepartmentName.SURGERY_OR]: 'SURG',
  [DepartmentName.INTENSIVE_CARE_UNIT]: 'ICU',
  [DepartmentName.NEONATAL_ICU]: 'NICU',
  [DepartmentName.LABOR_DELIVERY]: 'L&D',
  [DepartmentName.ORTHOPEDICS]: 'ORTHO',
  [DepartmentName.GASTROENTEROLOGY]: 'GI',
  [DepartmentName.NEUROLOGY]: 'NEURO',
  [DepartmentName.ONCOLOGY]: 'ONC',
  [DepartmentName.DERMATOLOGY]: 'DERM',
  [DepartmentName.UROLOGY]: 'URO',
  [DepartmentName.ENT]: 'ENT',
  [DepartmentName.OBSTETRICS_GYNECOLOGY]: 'OB/GYN',
  [DepartmentName.PHYSICAL_THERAPY]: 'PT',
  [DepartmentName.OCCUPATIONAL_THERAPY]: 'OT',
  [DepartmentName.SPEECH_THERAPY]: 'ST',
  [DepartmentName.BEHAVIORAL_HEALTH]: 'BH',
  [DepartmentName.NUTRITION_DIETETICS]: 'NUTR',
  [DepartmentName.SOCIAL_WORK]: 'SW',
  [DepartmentName.ADMINISTRATION_BILLING]: 'ADMIN',
  [DepartmentName.PHARMACY]: 'PHARM',
  [DepartmentName.LABORATORY]: 'LAB',
  [DepartmentName.ANESTHESIOLOGY]: 'ANES',
  [DepartmentName.PULMONOLOGY_SLEEP]: 'PULM',
  [DepartmentName.INFECTIOUS_DISEASE]: 'ID',
  [DepartmentName.ENDOCRINOLOGY]: 'ENDO',
  [DepartmentName.NEPHROLOGY_DIALYSIS]: 'NEPH',
  [DepartmentName.GERIATRICS]: 'GERI',
  [DepartmentName.REHABILITATION_MEDICINE]: 'REHAB',
  [DepartmentName.PAIN_MANAGEMENT]: 'PAIN',
  [DepartmentName.SLEEP_MEDICINE]: 'SLEEP',
  [DepartmentName.FAMILY_MEDICINE]: 'FM',
};

// =============================================================================
// PROCEDURE CODE TYPES
// =============================================================================

/**
 * Procedure Code Types - Standard medical coding systems
 * Used for categorizing and standardizing medical procedures, tests, and diagnoses
 */
export enum ProcedureCodeType {
  CPT4 = 'CPT4',           // Current Procedural Terminology, 4th Edition
  LOINC = 'LOINC',         // Logical Observation Identifiers Names and Codes
  SNOMED = 'SNOMED',       // Systematized Nomenclature of Medicine
  HCPCS = 'HCPCS',         // Healthcare Common Procedure Coding System
  ICD10 = 'ICD10',         // International Classification of Diseases, 10th Revision
  NDC = 'NDC',             // National Drug Code
  CVX = 'CVX',             // Vaccine Codes
  CUSTOM = 'Custom',       // Custom/internal codes
}

/**
 * Array of all procedure code types for UI dropdowns and validation
 */
export const PROCEDURE_CODE_TYPES = Object.values(ProcedureCodeType);

/**
 * Procedure code type descriptions for UI display
 */
export const PROCEDURE_CODE_TYPE_DESCRIPTIONS: Record<ProcedureCodeType, string> = {
  [ProcedureCodeType.CPT4]: 'Current Procedural Terminology, 4th Edition',
  [ProcedureCodeType.LOINC]: 'Logical Observation Identifiers Names and Codes',
  [ProcedureCodeType.SNOMED]: 'Systematized Nomenclature of Medicine',
  [ProcedureCodeType.HCPCS]: 'Healthcare Common Procedure Coding System',
  [ProcedureCodeType.ICD10]: 'International Classification of Diseases, 10th Revision',
  [ProcedureCodeType.NDC]: 'National Drug Code',
  [ProcedureCodeType.CVX]: 'Vaccine Codes',
  [ProcedureCodeType.CUSTOM]: 'Custom/Internal Codes',
};

// =============================================================================
// TAXONOMY CODE TYPES
// =============================================================================

/**
 * Taxonomy Code Types - Healthcare provider taxonomy classifications
 * Used for categorizing healthcare providers, facilities, and specialties
 */
export enum TaxonomyCodeType {
  PROVIDER = 'Provider Taxonomy',
  FACILITY = 'Facility Taxonomy',
  SPECIALTY = 'Specialty Taxonomy',
}

/**
 * Array of all taxonomy code types for UI dropdowns and validation
 */
export const TAXONOMY_CODE_TYPES = Object.values(TaxonomyCodeType);

/**
 * Taxonomy code type descriptions for UI display
 */
export const TAXONOMY_CODE_TYPE_DESCRIPTIONS: Record<TaxonomyCodeType, string> = {
  [TaxonomyCodeType.PROVIDER]: 'Healthcare Provider Classifications',
  [TaxonomyCodeType.FACILITY]: 'Healthcare Facility Types',
  [TaxonomyCodeType.SPECIALTY]: 'Healthcare Specialty Classifications',
};

// =============================================================================
// BILLING STATUSES
// =============================================================================

/**
 * Billing Status - Tracks the financial lifecycle of procedures
 */
export enum BillingStatus {
  PENDING = 'PENDING',
  BILLED = 'BILLED',
  PAID = 'PAID',
  DENIED = 'DENIED',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  RESUBMITTED = 'RESUBMITTED',
  PARTIAL = 'PARTIAL',
}

/**
 * Array of all billing statuses for UI dropdowns
 */
export const BILLING_STATUSES = Object.values(BillingStatus);

// =============================================================================
// ORDER TYPES
// =============================================================================

/**
 * Order Types - Categories of medical orders
 */
export enum OrderType {
  LAB = 'Lab',
  IMAGING = 'Imaging',
  PROCEDURE = 'Procedure',
  SURGICAL = 'Surgical',
  PHYSICAL_EXAM = 'Physical Exam',
  REFERRAL = 'Referral',
  GENERIC = 'Generic',
  INTERVENTION = 'Intervention',
  RISK_ASSESSMENT = 'Risk Assessment',
}

/**
 * Array of all order types for UI dropdowns
 */
export const ORDER_TYPES = Object.values(OrderType);

// =============================================================================
// PRIORITY LEVELS
// =============================================================================

/**
 * Priority Levels - Urgency classification for orders
 */
export enum PriorityLevel {
  STAT = 'STAT',           // Immediately
  ASAP = 'ASAP',           // As soon as possible
  ROUTINE = 'Routine',     // Normal priority
  URGENT = 'Urgent',       // High priority
  EMERGENCY = 'Emergency', // Critical
}

/**
 * Array of all priority levels for UI dropdowns
 */
export const PRIORITY_LEVELS = Object.values(PriorityLevel);

// =============================================================================
// USER ROLES
// =============================================================================

/**
 * User Roles - System access and permission levels
 */
export enum UserRole {
  ADMIN = 'admin',
  FACILITY_ADMIN = 'facility_admin',
  DOCTOR = 'doctor',
  NURSE = 'nurse',
  USER = 'user',
  ATTENDING_PHYSICIAN = 'attending_physician',
  CASE_MANAGER = 'case_manager',
  SOCIAL_WORKER = 'social_worker',
  PHARMACIST = 'pharmacist',
  PHYSICAL_THERAPIST = 'physical_therapist',
  OCCUPATIONAL_THERAPIST = 'occupational_therapist',
  SPEECH_LANGUAGE_PATHOLOGIST = 'speech_language_pathologist',
  DIETITIAN = 'dietitian',
  UNIT_CLERK = 'unit_clerk',
  BED_MANAGER = 'bed_manager',
  ADMISSION_CLERK = 'admission_clerk',
  BILLING_SPECIALIST = 'billing_specialist',
  BILLING = 'billing', // Legacy alias for BILLING_SPECIALIST
  PATIENT = 'patient',
  PATIENT_PORTAL = 'patient_portal',
}

/**
 * Array of all user roles for UI dropdowns
 */
export const USER_ROLES = Object.values(UserRole);

/**
 * User role display names for UI
 */
export const USER_ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'System Administrator',
  [UserRole.FACILITY_ADMIN]: 'Facility Administrator',
  [UserRole.DOCTOR]: 'Doctor',
  [UserRole.NURSE]: 'Nurse',
  [UserRole.USER]: 'User',
  [UserRole.ATTENDING_PHYSICIAN]: 'Attending Physician',
  [UserRole.CASE_MANAGER]: 'Case Manager',
  [UserRole.SOCIAL_WORKER]: 'Social Worker',
  [UserRole.PHARMACIST]: 'Pharmacist',
  [UserRole.PHYSICAL_THERAPIST]: 'Physical Therapist (PT)',
  [UserRole.OCCUPATIONAL_THERAPIST]: 'Occupational Therapist (OT)',
  [UserRole.SPEECH_LANGUAGE_PATHOLOGIST]: 'Speech-Language Pathologist (SLP)',
  [UserRole.DIETITIAN]: 'Dietitian',
  [UserRole.UNIT_CLERK]: 'Unit Clerk / Ward Secretary',
  [UserRole.BED_MANAGER]: 'Bed Manager / Transfer Coordinator',
  [UserRole.ADMISSION_CLERK]: 'Admission/Registration Clerk',
  [UserRole.BILLING_SPECIALIST]: 'Billing Specialist',
  [UserRole.BILLING]: 'Billing', // Legacy alias
  [UserRole.PATIENT]: 'Patient',
  [UserRole.PATIENT_PORTAL]: 'Patient Portal User',
};

/**
 * Multiple roles support - user can have multiple roles
 */
export type UserRoles = UserRole[];

/**
 * Utility functions for role checking
 */
export const RoleUtils = {
  /**
   * Check if user has a specific role
   */
  hasRole: (userRoles: UserRole[] | UserRole | undefined, targetRole: UserRole): boolean => {
    if (!userRoles) return false;
    if (Array.isArray(userRoles)) {
      return userRoles.includes(targetRole);
    }
    return userRoles === targetRole;
  },

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole: (userRoles: UserRole[] | UserRole | undefined, targetRoles: UserRole[]): boolean => {
    if (!userRoles) return false;
    if (Array.isArray(userRoles)) {
      return targetRoles.some(role => userRoles.includes(role));
    }
    return targetRoles.includes(userRoles);
  },

  /**
   * Check if user has all specified roles
   */
  hasAllRoles: (userRoles: UserRole[] | UserRole | undefined, targetRoles: UserRole[]): boolean => {
    if (!userRoles) return false;
    if (Array.isArray(userRoles)) {
      return targetRoles.every(role => userRoles.includes(role));
    }
    return targetRoles.length === 1 && targetRoles[0] === userRoles;
  },

  /**
   * Get highest priority role (admin > facility_admin > doctor > nurse > user)
   */
  getPrimaryRole: (userRoles: UserRole[] | UserRole | undefined): UserRole | null => {
    if (!userRoles) return null;
    if (!Array.isArray(userRoles)) return userRoles;
    
    const priorityOrder = [
      UserRole.ADMIN,
      UserRole.FACILITY_ADMIN,
      UserRole.DOCTOR,
      UserRole.ATTENDING_PHYSICIAN,
      UserRole.NURSE,
      UserRole.PHARMACIST,
      UserRole.CASE_MANAGER,
      UserRole.SOCIAL_WORKER,
      UserRole.PHYSICAL_THERAPIST,
      UserRole.OCCUPATIONAL_THERAPIST,
      UserRole.SPEECH_LANGUAGE_PATHOLOGIST,
      UserRole.DIETITIAN,
      UserRole.BED_MANAGER,
      UserRole.UNIT_CLERK,
      UserRole.ADMISSION_CLERK,
      UserRole.BILLING_SPECIALIST,
      UserRole.BILLING, // Legacy alias
      UserRole.USER,
      UserRole.PATIENT,
      UserRole.PATIENT_PORTAL
    ];

    for (const role of priorityOrder) {
      if (userRoles.includes(role)) return role;
    }
    return null;
  },

  /**
   * Format roles for display
   */
  formatRolesForDisplay: (userRoles: UserRole[] | UserRole | undefined): string => {
    if (!userRoles) return 'No roles assigned';
    if (!Array.isArray(userRoles)) return USER_ROLE_DISPLAY_NAMES[userRoles];
    
    if (userRoles.length === 0) return 'No roles assigned';
    if (userRoles.length === 1) return USER_ROLE_DISPLAY_NAMES[userRoles[0]];
    
    return userRoles.map(role => USER_ROLE_DISPLAY_NAMES[role]).join(', ');
  }
};

// =============================================================================
// INVENTORY CATEGORIES
// =============================================================================

/**
 * Inventory Item Categories - Classification of medical supplies and equipment
 */
export enum InventoryCategory {
  MEDICATION = 'MEDICATION',
  SURGICAL_TOOL = 'SURGICAL_TOOL',
  PPE = 'PPE',                    // Personal Protective Equipment
  CONSUMABLE = 'CONSUMABLE',
  EQUIPMENT = 'EQUIPMENT',
  OTHER = 'OTHER',
}

/**
 * Array of all inventory categories for UI dropdowns
 */
export const INVENTORY_CATEGORIES = Object.values(InventoryCategory);

/**
 * Inventory category display names for UI
 */
export const INVENTORY_CATEGORY_DISPLAY_NAMES: Record<InventoryCategory, string> = {
  [InventoryCategory.MEDICATION]: 'Medication',
  [InventoryCategory.SURGICAL_TOOL]: 'Surgical Tool',
  [InventoryCategory.PPE]: 'Personal Protective Equipment',
  [InventoryCategory.CONSUMABLE]: 'Consumable',
  [InventoryCategory.EQUIPMENT]: 'Equipment',
  [InventoryCategory.OTHER]: 'Other',
};

// =============================================================================
// PURCHASE ORDER STATUSES
// =============================================================================

/**
 * Purchase Order Status - Tracks procurement lifecycle
 */
export enum PurchaseOrderStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  ORDERED = 'ORDERED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
}

/**
 * Array of all purchase order statuses for UI dropdowns
 */
export const PURCHASE_ORDER_STATUSES = Object.values(PurchaseOrderStatus);

// =============================================================================
// INSURANCE TYPES
// =============================================================================

/**
 * Insurance Types - Patient insurance classification
 */
export enum InsuranceType {
  PRIVATE = 'private',
  MEDICARE = 'medicare',
  MEDICAID = 'medicaid',
  WORKERS_COMP = 'workers_comp',
  OTHER = 'other',
}

/**
 * Array of all insurance types for UI dropdowns
 */
export const INSURANCE_TYPES = Object.values(InsuranceType);

/**
 * Insurance type display names for UI
 */
export const INSURANCE_TYPE_DISPLAY_NAMES: Record<InsuranceType, string> = {
  [InsuranceType.PRIVATE]: 'Private Insurance',
  [InsuranceType.MEDICARE]: 'Medicare',
  [InsuranceType.MEDICAID]: 'Medicaid',
  [InsuranceType.WORKERS_COMP]: "Worker's Compensation",
  [InsuranceType.OTHER]: 'Other Insurance',
};

// =============================================================================
// CLAIM STATUSES
// =============================================================================

/**
 * Claim Status - Insurance claim processing status
 */
export enum ClaimStatus {
  PENDING = 'pending',
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
  DENIED = 'denied',
  RESUBMITTED = 'resubmitted',
  PARTIAL = 'partial',
}

/**
 * Array of all claim statuses for UI dropdowns
 */
export const CLAIM_STATUSES = Object.values(ClaimStatus);

/**
 * Claim status display names and colors for UI
 */
export const CLAIM_STATUS_DISPLAY: Record<ClaimStatus, { label: string; color: string }> = {
  [ClaimStatus.PENDING]: { label: 'Pending', color: 'warning' },
  [ClaimStatus.SUBMITTED]: { label: 'Submitted', color: 'info' },
  [ClaimStatus.APPROVED]: { label: 'Approved', color: 'success' },
  [ClaimStatus.DENIED]: { label: 'Denied', color: 'error' },
  [ClaimStatus.RESUBMITTED]: { label: 'Resubmitted', color: 'warning' },
  [ClaimStatus.PARTIAL]: { label: 'Partially Approved', color: 'warning' },
};

// =============================================================================
// CARE PLAN STATUSES
// =============================================================================

/**
 * Care Plan Status - Patient care plan lifecycle
 */
export enum CarePlanStatus {
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  ON_HOLD = 'On Hold',
  DRAFT = 'Draft',
  ARCHIVED = 'Archived',
}

/**
 * Array of all care plan statuses for UI dropdowns
 */
export const CARE_PLAN_STATUSES = Object.values(CarePlanStatus);

// =============================================================================
// REFERRAL STATUSES
// =============================================================================

/**
 * Referral Status - Patient referral tracking
 */
export enum ReferralStatus {
  PENDING = 'Pending',
  SCHEDULED = 'Scheduled',
  SENT = 'Sent',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
}

/**
 * Array of all referral statuses for UI dropdowns
 */
export const REFERRAL_STATUSES = Object.values(ReferralStatus);

// =============================================================================
// MEDICATION ROUTES
// =============================================================================

/**
 * Medication Administration Routes
 */
export enum MedicationRoute {
  ORAL = 'Oral',
  INTRAVENOUS = 'Intravenous',
  INTRAMUSCULAR = 'Intramuscular',
  SUBCUTANEOUS = 'Subcutaneous',
  TOPICAL = 'Topical',
  INHALATION = 'Inhalation',
  RECTAL = 'Rectal',
  OPHTHALMIC = 'Ophthalmic',
  OTIC = 'Otic',
  NASAL = 'Nasal',
}

/**
 * Array of all medication routes for UI dropdowns
 */
export const MEDICATION_ROUTES = Object.values(MedicationRoute);

// =============================================================================
// LAB INTEGRATION OPTIONS
// =============================================================================

/**
 * Lab Integration Usage Options
 */
export enum LabIntegrationUsage {
  DEBUGGING = 'Debugging',
  PRODUCTION = 'Production',
}

/**
 * Lab Integration Protocol Options
 */
export enum LabIntegrationProtocol {
  SFTP = 'SFTP',
  FILE_STORAGE = 'File Storage',
  API = 'API',
}

/**
 * Lab Integration Direction Options
 */
export enum LabIntegrationDirection {
  BIDIRECTIONAL = 'Bidirectional',
  INBOUND = 'Inbound',
  OUTBOUND = 'Outbound',
}

// =============================================================================
// CHECKUP OPTIONS
// =============================================================================

/**
 * Checkup/Assessment Options
 */
export enum CheckupOption {
  BLOOD_PRESSURE = 'BP',
  BMI = 'BMI',
  VISION = 'VISION',
  HEARING = 'HEARING',
  GLUCOSE = 'GLUCOSE',
  HEIGHT = 'HEIGHT',
  WEIGHT = 'WEIGHT',
  TEMPERATURE = 'TEMPERATURE',
  PULSE = 'PULSE',
  RESPIRATORY_RATE = 'RESPIRATORY_RATE',
}

/**
 * Array of all checkup options for UI dropdowns
 */
export const CHECKUP_OPTIONS = Object.values(CheckupOption);

/**
 * Checkup option display names for UI
 */
export const CHECKUP_OPTION_DISPLAY_NAMES: Record<CheckupOption, string> = {
  [CheckupOption.BLOOD_PRESSURE]: 'Blood Pressure',
  [CheckupOption.BMI]: 'BMI',
  [CheckupOption.VISION]: 'Vision Screening',
  [CheckupOption.HEARING]: 'Hearing Screening',
  [CheckupOption.GLUCOSE]: 'Glucose Screening',
  [CheckupOption.HEIGHT]: 'Height',
  [CheckupOption.WEIGHT]: 'Weight',
  [CheckupOption.TEMPERATURE]: 'Temperature',
  [CheckupOption.PULSE]: 'Pulse',
  [CheckupOption.RESPIRATORY_RATE]: 'Respiratory Rate',
};

// =============================================================================
// OPERATIONS
// =============================================================================

/**
 * Database Operations - Used for permissions and audit trails
 */
export enum Operation {
  READ = 'read',
  WRITE = 'write',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
}

/**
 * Array of all operations for UI dropdowns
 */
export const OPERATIONS = Object.values(Operation);

// =============================================================================
// COLLECTIONS
// =============================================================================

/**
 * Firestore Collections - Database collection names
 */
export enum Collection {
  APPOINTMENTS = 'appointments',
  ALERTS_NOTIFICATIONS = 'AlertsNotifications',
  AUDIT = 'audit',
  BILLS = 'bills',
  FACILITIES = 'facilities',
  FACILITY_GROUPS = 'facilityGroups',
  MESSAGES = 'messages',
  MESSAGE_THREADS = 'messageThreads',
  PATIENTS = 'patients',
  PRESCRIPTIONS = 'prescriptions',
  PROCEDURES = 'procedures',
  PROCEDURE_CODES = 'procedure_codes',
  TAXONOMY_CODES = 'taxonomy_codes',
  TRANSMISSION_LOG = 'transmission_log',
  USER_FACILITIES = 'user_facilities',
  USERS = 'users',
  ENCOUNTERS = 'encounters',
  INVENTORY = 'inventory',
  PURCHASE_ORDERS = 'purchase_orders',
  SUPPLIERS = 'suppliers',
  CARE_PLANS = 'care_plans',
  REFERRALS = 'referrals',
  MEDICAL_RECORDS = 'medical_records',
}

/**
 * Array of all collections for UI dropdowns
 */
export const COLLECTIONS = Object.values(Collection);

// =============================================================================
// JOB TYPES
// =============================================================================

/**
 * Job Types - Background job classifications
 */
export enum JobType {
  PROCESS_RECORD_SHARE = 'PROCESS_RECORD_SHARE',
  SYNC_PATIENT_DATA = 'SYNC_PATIENT_DATA',
}

/**
 * Job Status - Background job lifecycle
 */
export enum JobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

// =============================================================================
// ENCOUNTER TYPES
// =============================================================================

export enum EncounterType {
  OUTPATIENT_VISIT = 'outpatient',
  EMERGENCY_VISIT = 'emergency',
  INPATIENT_ADMISSION = 'inpatient',
  TELEHEALTH = 'telehealth',
  OBSERVATION = 'observation',
  SURGERY = 'surgery',
  HOME_HEALTH = 'homeHealth',
}

// =============================================================================
// ENCOUNTER STATUS
// =============================================================================

export enum EncounterStatus {
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  TRANSFERRED = 'transferred',
  DISCHARGED = 'discharged',
  CANCELLED = 'cancelled',
}

export const ENCOUNTER_STATUSES = Object.values(EncounterStatus);

// =============================================================================
// APPOINTMENT TYPES
// =============================================================================

export enum AppointmentType {
  WALK_IN = 'Walk-In',
  EMERGENCY = 'Emergency',
  URGENT = 'Urgent',
  TELEHEALTH_VISIT = 'Telehealth Visit',
  FOLLOW_UP = 'Follow-Up',
  INPATIENT_ADMISSION = 'Inpatient Admission',
  SCHEDULED_OUTPATIENT = 'Scheduled (Outpatient)',
  REFERRAL = 'Referral',
  TRANSFER_IN = 'Transfer In',
  VACCINATION = 'Vaccination',
  PHYSICAL_ANNUAL_EXAM = 'Annual Exam',
  CONSULTATION = 'Consultation',
  PROCEDURE_VISIT = 'Procedure Visit',
  PRE_OP_EVALUATION = 'Pre-Op Evaluation',
  POST_OP_VISIT = 'Post-Op Visit',
  CHRONIC_CARE_VISIT = 'Chronic Care Visit',
  BEHAVIORAL_HEALTH = 'Behavioral Health',
  LAB_ONLY_VISIT = 'Lab-Only Visit', 
  OBSERVATION = 'Observation',
  // Oncology appointment types
  CHEMOTHERAPY_INFUSION = 'Chemotherapy Infusion',
  RADIATION_THERAPY = 'Radiation Therapy',
  ONCOLOGY_CONSULTATION = 'Oncology Consultation',
  ONCOLOGY_FOLLOW_UP = 'Oncology Follow-Up',
  TUMOR_BOARD = 'Tumor Board',
  SURVIVORSHIP_VISIT = 'Survivorship Visit'
}

/**
 * Array of all appointment types for UI dropdowns
 */
export const APPOINTMENT_TYPES: string[] = [
  AppointmentType.WALK_IN,
  AppointmentType.EMERGENCY,
  AppointmentType.URGENT,
  AppointmentType.FOLLOW_UP,
  AppointmentType.INPATIENT_ADMISSION,
  AppointmentType.SCHEDULED_OUTPATIENT,
  AppointmentType.REFERRAL,
  AppointmentType.TRANSFER_IN,
  AppointmentType.VACCINATION,
  AppointmentType.PHYSICAL_ANNUAL_EXAM,
  AppointmentType.CONSULTATION,
  AppointmentType.PROCEDURE_VISIT,
  AppointmentType.PRE_OP_EVALUATION,
  AppointmentType.POST_OP_VISIT,
  AppointmentType.CHRONIC_CARE_VISIT,
  AppointmentType.BEHAVIORAL_HEALTH,
  AppointmentType.LAB_ONLY_VISIT,
  AppointmentType.TELEHEALTH_VISIT,
  // Optionally include Observation:
  AppointmentType.OBSERVATION,
  // Oncology appointment types
  AppointmentType.CHEMOTHERAPY_INFUSION,
  AppointmentType.RADIATION_THERAPY,
  AppointmentType.ONCOLOGY_CONSULTATION,
  AppointmentType.ONCOLOGY_FOLLOW_UP,
  AppointmentType.TUMOR_BOARD,
  AppointmentType.SURVIVORSHIP_VISIT,
];

/**
 * Appointment type icon mapping for Material-UI icons
 * Maps each appointment type to an appropriate icon name
 */
export const APPOINTMENT_TYPE_ICONS: Record<AppointmentType, string> = {
  [AppointmentType.WALK_IN]: 'DirectionsWalk',
  [AppointmentType.EMERGENCY]: 'Emergency',
  [AppointmentType.URGENT]: 'PriorityHigh',
  [AppointmentType.FOLLOW_UP]: 'Loop',
  [AppointmentType.INPATIENT_ADMISSION]: 'Hotel',
  [AppointmentType.SCHEDULED_OUTPATIENT]: 'Event',
  [AppointmentType.REFERRAL]: 'Forward',
  [AppointmentType.TRANSFER_IN]: 'SwapHoriz',
  [AppointmentType.VACCINATION]: 'Vaccines',
  [AppointmentType.PHYSICAL_ANNUAL_EXAM]: 'MedicalServices',
  [AppointmentType.CONSULTATION]: 'Forum',
  [AppointmentType.PROCEDURE_VISIT]: 'MedicalServices',
  [AppointmentType.PRE_OP_EVALUATION]: 'MedicalServices',
  [AppointmentType.POST_OP_VISIT]: 'MedicalServices',
  [AppointmentType.CHRONIC_CARE_VISIT]: 'MedicalServices',
  [AppointmentType.BEHAVIORAL_HEALTH]: 'Psychology',
  [AppointmentType.LAB_ONLY_VISIT]: 'MedicalServices',
  [AppointmentType.TELEHEALTH_VISIT]: 'Videocam',
  [AppointmentType.OBSERVATION]: 'Visibility',
  // Oncology appointment type icons
  [AppointmentType.CHEMOTHERAPY_INFUSION]: 'Science',
  [AppointmentType.RADIATION_THERAPY]: 'Science',
  [AppointmentType.ONCOLOGY_CONSULTATION]: 'Science',
  [AppointmentType.ONCOLOGY_FOLLOW_UP]: 'Science',
  [AppointmentType.TUMOR_BOARD]: 'Groups',
  [AppointmentType.SURVIVORSHIP_VISIT]: 'Favorite',
};



// =============================================================================
// APPOINTMENT STATUS TYPES
// =============================================================================

export enum AppointmentStatus {
  SCHEDULED = 'scheduled',
  CONFIRMED = 'confirmed',
  CHECKED_IN = 'checked-in',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

/**
 * Array of all appointment statuses for UI dropdowns
 */
export const APPOINTMENT_STATUSES: string[] = [
  AppointmentStatus.SCHEDULED,
  AppointmentStatus.CONFIRMED,
  AppointmentStatus.CHECKED_IN,
  AppointmentStatus.COMPLETED,
  AppointmentStatus.CANCELLED,
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get display name for any enum value
 * @param enumObj - The enum object
 * @param value - The enum value
 * @param displayMap - Optional display name mapping
 * @returns The display name or the original value
 */
export function getDisplayName<T extends string>(
  enumObj: Record<string, T>,
  value: T,
  displayMap?: Record<T, string>
): string {
  if (displayMap && displayMap[value]) {
    return displayMap[value];
  }
  return value;
}

/**
 * Get all values from an enum as an array
 * @param enumObj - The enum object
 * @returns Array of enum values
 */
export function getEnumValues<T extends string>(enumObj: Record<string, T>): T[] {
  return Object.values(enumObj);
}

/**
 * Check if a value is a valid enum value
 * @param enumObj - The enum object
 * @param value - The value to check
 * @returns True if the value is valid
 */
export function isValidEnumValue<T extends string>(
  enumObj: Record<string, T>,
  value: string
): value is T {
  return Object.values(enumObj).includes(value as T);
}

export enum AdmissionType {
  EMERGENCY = 'Emergency',
  URGENT = 'Urgent',
  ELECTIVE = 'Elective',
  NEWBORN = 'Newborn',
  TRAUMA = 'Trauma',
  OBSERVATION = 'Observation',
  TRANSFER = 'Transfer',
  READMISSION = 'Readmission',
  LABOR_DELIVERY = 'Labor & Delivery',
  PSYCHIATRIC = 'Psychiatric',
  SKILLED_NURSING_REHAB = 'Skilled Nursing / Rehab',
}

export const ADMISSION_TYPES = Object.values(AdmissionType);

// =============================================================================
// ROOM TYPES
// =============================================================================

/**
 * Room Types - Classification of healthcare facility rooms
 */
export enum RoomType {
  EXAM_ROOM = 'Exam Room',
  STANDALONE_ROOM = 'Stand-alone Room',
  OPERATING_ROOM = 'Operating Room',
  ICU_BED = 'ICU Bed',
  WARD = 'Ward',
  SUITE = 'Suite',
  LAB = 'Lab',
  IMAGING = 'Imaging',
  THERAPY = 'Therapy',
  OTHER = 'Other',
}

/**
 * Array of all room types for UI dropdowns
 */
export const ROOM_TYPES = Object.values(RoomType);

/**
 * Room type descriptions for UI display
 */
export const ROOM_TYPE_DESCRIPTIONS: Record<RoomType, string> = {
  [RoomType.EXAM_ROOM]: 'Standard examination room for patient consultations',
  [RoomType.STANDALONE_ROOM]: 'Room-level assignment without beds (for outpatient visits)',
  [RoomType.OPERATING_ROOM]: 'Surgical suite for procedures and operations',
  [RoomType.ICU_BED]: 'Intensive care unit bed for critical patients',
  [RoomType.WARD]: 'General ward room for multiple patients',
  [RoomType.SUITE]: 'Private suite for VIP or special care patients',
  [RoomType.LAB]: 'Laboratory room for testing and analysis',
  [RoomType.IMAGING]: 'Imaging room for X-rays, CT scans, MRIs, etc.',
  [RoomType.THERAPY]: 'Therapy room for physical, occupational, or speech therapy',
  [RoomType.OTHER]: 'Other specialized room types',
};

// =============================================================================
// ROOM STATUSES
// =============================================================================

/**
 * Room Status - Current operational status of a room
 */
export enum RoomStatus {
  AVAILABLE = 'available',
  OCCUPIED = 'occupied',
  RESERVED = 'reserved',
  MAINTENANCE = 'maintenance',
  OUT_OF_SERVICE = 'out_of_service',
}

/**
 * Array of all room statuses for UI dropdowns
 */
export const ROOM_STATUSES = Object.values(RoomStatus);

/**
 * Room status display names for UI
 */
export const ROOM_STATUS_DISPLAY_NAMES: Record<RoomStatus, string> = {
  [RoomStatus.AVAILABLE]: 'Available',
  [RoomStatus.OCCUPIED]: 'Occupied',
  [RoomStatus.RESERVED]: 'Reserved',
  [RoomStatus.MAINTENANCE]: 'Maintenance',
  [RoomStatus.OUT_OF_SERVICE]: 'Out of Service',
};

/**
 * Room status colors for UI display
 */
export const ROOM_STATUS_COLORS: Record<RoomStatus, string> = {
  [RoomStatus.AVAILABLE]: 'success',
  [RoomStatus.OCCUPIED]: 'error',
  [RoomStatus.RESERVED]: 'warning',
  [RoomStatus.MAINTENANCE]: 'info',
  [RoomStatus.OUT_OF_SERVICE]: 'default',
};

// =============================================================================
// BED TYPES
// =============================================================================

/**
 * Bed Types - Classification of healthcare facility beds
 */
export enum BedType {
  STANDARD = 'Standard',
  ICU = 'ICU',
  ISOLATION = 'Isolation',
  PEDIATRIC = 'Pediatric',
  MATERNITY = 'Maternity',
  BARIATRIC = 'Bariatric',
  PSYCHIATRIC = 'Psychiatric',
  OTHER = 'Other',
}

/**
 * Array of all bed types for UI dropdowns
 */
export const BED_TYPES = Object.values(BedType);

/**
 * Bed type descriptions for UI display
 */
export const BED_TYPE_DESCRIPTIONS: Record<BedType, string> = {
  [BedType.STANDARD]: 'Standard hospital bed for general patients',
  [BedType.ICU]: 'Intensive care unit bed with advanced monitoring',
  [BedType.ISOLATION]: 'Isolation bed for infectious disease patients',
  [BedType.PEDIATRIC]: 'Specialized bed for pediatric patients',
  [BedType.MATERNITY]: 'Maternity bed for labor and delivery',
  [BedType.BARIATRIC]: 'Bariatric bed for patients with weight considerations',
  [BedType.PSYCHIATRIC]: 'Psychiatric bed for mental health patients',
  [BedType.OTHER]: 'Other specialized bed types',
};

// =============================================================================
// BED STATUSES
// =============================================================================

/**
 * Bed Status - Current operational status of a bed
 */
export enum BedStatus {
  AVAILABLE = 'available',
  OCCUPIED = 'occupied',
  RESERVED = 'reserved',
  MAINTENANCE = 'maintenance',
  OUT_OF_SERVICE = 'out_of_service',
}

/**
 * Array of all bed statuses for UI dropdowns
 */
export const BED_STATUSES = Object.values(BedStatus);

/**
 * Bed status display names for UI
 */
export const BED_STATUS_DISPLAY_NAMES: Record<BedStatus, string> = {
  [BedStatus.AVAILABLE]: 'Available',
  [BedStatus.OCCUPIED]: 'Occupied',
  [BedStatus.RESERVED]: 'Reserved',
  [BedStatus.MAINTENANCE]: 'Maintenance',
  [BedStatus.OUT_OF_SERVICE]: 'Out of Service',
};

/**
 * Bed status colors for UI display
 */
export const BED_STATUS_COLORS: Record<BedStatus, string> = {
  [BedStatus.AVAILABLE]: 'success',
  [BedStatus.OCCUPIED]: 'error',
  [BedStatus.RESERVED]: 'warning',
  [BedStatus.MAINTENANCE]: 'info',
  [BedStatus.OUT_OF_SERVICE]: 'default',
};

// =============================================================================
// ASSIGNMENT STATUSES
// =============================================================================

/**
 * Assignment Status - Status of bed or room assignments
 */
export enum AssignmentStatus {
  ACTIVE = 'active',
  DISCHARGED = 'discharged',
  TRANSFERRED = 'transferred',
  CANCELLED = 'cancelled',
}

/**
 * Array of all assignment statuses for UI dropdowns
 */
export const ASSIGNMENT_STATUSES = Object.values(AssignmentStatus);

/**
 * Assignment status display names for UI
 */
export const ASSIGNMENT_STATUS_DISPLAY_NAMES: Record<AssignmentStatus, string> = {
  [AssignmentStatus.ACTIVE]: 'Active',
  [AssignmentStatus.DISCHARGED]: 'Discharged',
  [AssignmentStatus.TRANSFERRED]: 'Transferred',
  [AssignmentStatus.CANCELLED]: 'Cancelled',
};

/**
 * Assignment status colors for UI display
 */
export const ASSIGNMENT_STATUS_COLORS: Record<AssignmentStatus, string> = {
  [AssignmentStatus.ACTIVE]: 'success',
  [AssignmentStatus.DISCHARGED]: 'info',
  [AssignmentStatus.TRANSFERRED]: 'warning',
  [AssignmentStatus.CANCELLED]: 'error',
};

// =============================================================================
// ASSIGNMENT EVENT TYPES
// =============================================================================

/**
 * Assignment Event Types - Types of events in assignment history
 */
export enum AssignmentEventType {
  ASSIGNED = 'assigned',
  DISCHARGED = 'discharged',
  TRANSFERRED = 'transferred',
  CANCELLED = 'cancelled',
  UPDATED = 'updated',
}

/**
 * Array of all assignment event types for UI dropdowns
 */
export const ASSIGNMENT_EVENT_TYPES = Object.values(AssignmentEventType);

/**
 * Assignment event type display names for UI
 */
export const ASSIGNMENT_EVENT_TYPE_DISPLAY_NAMES: Record<AssignmentEventType, string> = {
  [AssignmentEventType.ASSIGNED]: 'Assigned',
  [AssignmentEventType.DISCHARGED]: 'Discharged',
  [AssignmentEventType.TRANSFERRED]: 'Transferred',
  [AssignmentEventType.CANCELLED]: 'Cancelled',
  [AssignmentEventType.UPDATED]: 'Updated',
};

// =============================================================================
// ROOM/BED STATUS OPTIONS (for forms)
// =============================================================================

/**
 * Room status options for form dropdowns
 */
export const ROOM_STATUS_OPTIONS = [
  { value: RoomStatus.AVAILABLE, label: ROOM_STATUS_DISPLAY_NAMES[RoomStatus.AVAILABLE] },
  { value: RoomStatus.OCCUPIED, label: ROOM_STATUS_DISPLAY_NAMES[RoomStatus.OCCUPIED] },
  { value: RoomStatus.RESERVED, label: ROOM_STATUS_DISPLAY_NAMES[RoomStatus.RESERVED] },
  { value: RoomStatus.MAINTENANCE, label: ROOM_STATUS_DISPLAY_NAMES[RoomStatus.MAINTENANCE] },
  { value: RoomStatus.OUT_OF_SERVICE, label: ROOM_STATUS_DISPLAY_NAMES[RoomStatus.OUT_OF_SERVICE] },
];

/**
 * Bed status options for form dropdowns
 */
export const BED_STATUS_OPTIONS = [
  { value: BedStatus.AVAILABLE, label: BED_STATUS_DISPLAY_NAMES[BedStatus.AVAILABLE] },
  { value: BedStatus.OCCUPIED, label: BED_STATUS_DISPLAY_NAMES[BedStatus.OCCUPIED] },
  { value: BedStatus.RESERVED, label: BED_STATUS_DISPLAY_NAMES[BedStatus.RESERVED] },
  { value: BedStatus.MAINTENANCE, label: BED_STATUS_DISPLAY_NAMES[BedStatus.MAINTENANCE] },
  { value: BedStatus.OUT_OF_SERVICE, label: BED_STATUS_DISPLAY_NAMES[BedStatus.OUT_OF_SERVICE] },
];

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Route Paths - Centralized route path constants for navigation
 */
export enum RoutePath {
  PATIENTS = '/patients',
  ENCOUNTERS = '/encounters',
  // Add more as needed
} 

// =============================================================================
// API SCOPES
// =============================================================================

/**
 * API Scopes - Permission scopes for external API access
 */
export enum ApiScope {
  PATIENTS_READ = 'patients:read',
  PATIENTS_WRITE = 'patients:write',
  RECORDS_READ = 'records:read',
  RECORDS_WRITE = 'records:write',
  RECORDS_SHARE = 'records:share',
  IMMUNIZATIONS_READ = 'immunizations:read',
  IMMUNIZATIONS_WRITE = 'immunizations:write',
  JOBS_READ = 'jobs:read',
  JOBS_WRITE = 'jobs:write',
  ELIGIBILITY_READ = 'eligibility:read',
  ELIGIBILITY_WRITE = 'eligibility:write',
  BILLING_READ = 'billing:read',
  BILLING_WRITE = 'billing:write',
  PATIENT_AUTH = 'patient:auth',
  APPOINTMENTS_READ = 'appointments:read',
  APPOINTMENTS_WRITE = 'appointments:write',
  LAB_RESULTS_READ = 'lab-results:read',
  LAB_RESULTS_WRITE = 'lab-results:write',
  PROVIDERS_READ = 'providers:read'

}

/**
 * Array of all API scopes for UI dropdowns
 */
export const API_SCOPES = Object.values(ApiScope);

// =============================================================================
// WORKFLOW STATUS
// =============================================================================

/**
 * Workflow Status - Status of workflow steps and tasks
 */
export enum WorkflowStatus {
  PENDING = 'pending',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  OVERDUE = 'overdue',
  CANCELLED = 'cancelled',
  SKIPPED = 'skipped',
}

/**
 * Array of all workflow statuses for UI dropdowns
 */
export const WORKFLOW_STATUSES = Object.values(WorkflowStatus);

/**
 * Workflow status display names for UI
 */
export const WORKFLOW_STATUS_DISPLAY_NAMES: Record<WorkflowStatus, string> = {
  [WorkflowStatus.PENDING]: 'Pending',
  [WorkflowStatus.IN_PROGRESS]: 'In Progress',
  [WorkflowStatus.COMPLETED]: 'Completed',
  [WorkflowStatus.OVERDUE]: 'Overdue',
  [WorkflowStatus.CANCELLED]: 'Cancelled',
  [WorkflowStatus.SKIPPED]: 'Skipped',
};

/**
 * Workflow status colors for UI display
 */
export const WORKFLOW_STATUS_COLORS: Record<WorkflowStatus, string> = {
  [WorkflowStatus.PENDING]: 'warning',
  [WorkflowStatus.IN_PROGRESS]: 'info',
  [WorkflowStatus.COMPLETED]: 'success',
  [WorkflowStatus.OVERDUE]: 'error',
  [WorkflowStatus.CANCELLED]: 'default',
  [WorkflowStatus.SKIPPED]: 'default',
};

// =============================================================================
// ONCOLOGY MODULE ENUMS
// =============================================================================

// -----------------------------------------------------------------------------
// CANCER TYPES
// -----------------------------------------------------------------------------

/**
 * Cancer Types - Major cancer categories based on ICD-O-3 morphology
 */
export enum CancerType {
  BREAST = 'Breast Cancer',
  LUNG = 'Lung Cancer',
  COLORECTAL = 'Colorectal Cancer',
  PROSTATE = 'Prostate Cancer',
  MELANOMA = 'Melanoma',
  BLADDER = 'Bladder Cancer',
  KIDNEY = 'Kidney Cancer',
  PANCREATIC = 'Pancreatic Cancer',
  LEUKEMIA = 'Leukemia',
  LYMPHOMA = 'Lymphoma',
  OVARIAN = 'Ovarian Cancer',
  UTERINE = 'Uterine Cancer',
  CERVICAL = 'Cervical Cancer',
  THYROID = 'Thyroid Cancer',
  LIVER = 'Liver Cancer',
  ESOPHAGEAL = 'Esophageal Cancer',
  GASTRIC = 'Gastric Cancer',
  HEAD_NECK = 'Head and Neck Cancer',
  BRAIN = 'Brain Cancer',
  SARCOMA = 'Sarcoma',
  MULTIPLE_MYELOMA = 'Multiple Myeloma',
  TESTICULAR = 'Testicular Cancer',
  OTHER = 'Other',
}

export const CANCER_TYPES = Object.values(CancerType);

// -----------------------------------------------------------------------------
// TNM STAGING
// -----------------------------------------------------------------------------

/**
 * T Stage - Primary tumor size/extent (AJCC 8th Edition)
 */
export enum TStage {
  TX = 'TX',   // Primary tumor cannot be assessed
  T0 = 'T0',   // No evidence of primary tumor
  TIS = 'Tis', // Carcinoma in situ
  T1 = 'T1',   // Tumor ≤ 2 cm
  T1A = 'T1a', // Tumor ≤ 0.5 cm
  T1B = 'T1b', // Tumor > 0.5 cm and ≤ 1 cm
  T1C = 'T1c', // Tumor > 1 cm and ≤ 2 cm
  T2 = 'T2',   // Tumor > 2 cm and ≤ 5 cm
  T2A = 'T2a', // Tumor > 2 cm and ≤ 3 cm
  T2B = 'T2b', // Tumor > 3 cm and ≤ 5 cm
  T3 = 'T3',   // Tumor > 5 cm
  T4 = 'T4',   // Tumor of any size with direct extension
  T4A = 'T4a', // Extension to chest wall
  T4B = 'T4b', // Extension to skin
  T4C = 'T4c', // Both T4a and T4b
  T4D = 'T4d', // Inflammatory carcinoma
}

export const T_STAGES = Object.values(TStage);

export const T_STAGE_DESCRIPTIONS: Record<TStage, string> = {
  [TStage.TX]: 'Primary tumor cannot be assessed',
  [TStage.T0]: 'No evidence of primary tumor',
  [TStage.TIS]: 'Carcinoma in situ',
  [TStage.T1]: 'Tumor ≤ 2 cm in greatest dimension',
  [TStage.T1A]: 'Tumor ≤ 0.5 cm',
  [TStage.T1B]: 'Tumor > 0.5 cm and ≤ 1 cm',
  [TStage.T1C]: 'Tumor > 1 cm and ≤ 2 cm',
  [TStage.T2]: 'Tumor > 2 cm and ≤ 5 cm',
  [TStage.T2A]: 'Tumor > 2 cm and ≤ 3 cm',
  [TStage.T2B]: 'Tumor > 3 cm and ≤ 5 cm',
  [TStage.T3]: 'Tumor > 5 cm in greatest dimension',
  [TStage.T4]: 'Tumor of any size with direct extension',
  [TStage.T4A]: 'Extension to chest wall',
  [TStage.T4B]: 'Extension to skin',
  [TStage.T4C]: 'Both T4a and T4b',
  [TStage.T4D]: 'Inflammatory carcinoma',
};

/**
 * N Stage - Regional lymph node involvement
 */
export enum NStage {
  NX = 'NX',   // Regional lymph nodes cannot be assessed
  N0 = 'N0',   // No regional lymph node metastasis
  N1 = 'N1',   // Metastasis in 1-3 axillary lymph nodes
  N1A = 'N1a', // Micrometastases
  N1B = 'N1b', // Macrometastases
  N1C = 'N1c', // Internal mammary nodes
  N2 = 'N2',   // Metastasis in 4-9 axillary lymph nodes
  N2A = 'N2a', // Fixed axillary nodes
  N2B = 'N2b', // Internal mammary nodes only
  N3 = 'N3',   // Metastasis in ≥10 axillary lymph nodes
  N3A = 'N3a', // Infraclavicular nodes
  N3B = 'N3b', // Internal mammary and axillary nodes
  N3C = 'N3c', // Supraclavicular nodes
}

export const N_STAGES = Object.values(NStage);

export const N_STAGE_DESCRIPTIONS: Record<NStage, string> = {
  [NStage.NX]: 'Regional lymph nodes cannot be assessed',
  [NStage.N0]: 'No regional lymph node metastasis',
  [NStage.N1]: 'Metastasis in 1-3 axillary lymph nodes',
  [NStage.N1A]: 'Micrometastases (> 0.2 mm and/or > 200 cells)',
  [NStage.N1B]: 'Macrometastases (> 2 mm)',
  [NStage.N1C]: 'Internal mammary nodes with micrometastases',
  [NStage.N2]: 'Metastasis in 4-9 axillary lymph nodes',
  [NStage.N2A]: 'Fixed or matted axillary nodes',
  [NStage.N2B]: 'Internal mammary nodes only',
  [NStage.N3]: 'Metastasis in ≥10 axillary lymph nodes',
  [NStage.N3A]: 'Infraclavicular lymph nodes',
  [NStage.N3B]: 'Internal mammary and axillary lymph nodes',
  [NStage.N3C]: 'Supraclavicular lymph nodes',
};

/**
 * M Stage - Distant metastasis
 */
export enum MStage {
  MX = 'MX',   // Distant metastasis cannot be assessed
  M0 = 'M0',   // No distant metastasis
  M1 = 'M1',   // Distant metastasis present
  M1A = 'M1a', // Single extrathoracic metastasis
  M1B = 'M1b', // Multiple extrathoracic metastases
  M1C = 'M1c', // Both intrathoracic and extrathoracic
}

export const M_STAGES = Object.values(MStage);

export const M_STAGE_DESCRIPTIONS: Record<MStage, string> = {
  [MStage.MX]: 'Distant metastasis cannot be assessed',
  [MStage.M0]: 'No distant metastasis',
  [MStage.M1]: 'Distant metastasis present',
  [MStage.M1A]: 'Single extrathoracic metastasis',
  [MStage.M1B]: 'Multiple extrathoracic metastases in single organ',
  [MStage.M1C]: 'Multiple extrathoracic metastases in multiple organs',
};

// -----------------------------------------------------------------------------
// AJCC STAGING
// -----------------------------------------------------------------------------

/**
 * AJCC Stage - Overall cancer stage (8th Edition)
 */
export enum AJCCStage {
  STAGE_0 = 'Stage 0',
  STAGE_I = 'Stage I',
  STAGE_IA = 'Stage IA',
  STAGE_IA1 = 'Stage IA1',
  STAGE_IA2 = 'Stage IA2',
  STAGE_IA3 = 'Stage IA3',
  STAGE_IB = 'Stage IB',
  STAGE_II = 'Stage II',
  STAGE_IIA = 'Stage IIA',
  STAGE_IIB = 'Stage IIB',
  STAGE_III = 'Stage III',
  STAGE_IIIA = 'Stage IIIA',
  STAGE_IIIB = 'Stage IIIB',
  STAGE_IIIC = 'Stage IIIC',
  STAGE_IV = 'Stage IV',
  STAGE_IVA = 'Stage IVA',
  STAGE_IVB = 'Stage IVB',
  OCCULT = 'Occult Carcinoma',
}

export const AJCC_STAGES = Object.values(AJCCStage);

export const AJCC_STAGE_DESCRIPTIONS: Record<AJCCStage, string> = {
  [AJCCStage.STAGE_0]: 'Carcinoma in situ (abnormal cells present but not spread)',
  [AJCCStage.STAGE_I]: 'Small tumor, no lymph node involvement',
  [AJCCStage.STAGE_IA]: 'Very small tumor (≤ 2 cm)',
  [AJCCStage.STAGE_IA1]: 'Tumor ≤ 1 cm',
  [AJCCStage.STAGE_IA2]: 'Tumor > 1 cm and ≤ 2 cm',
  [AJCCStage.STAGE_IA3]: 'Tumor > 2 cm and ≤ 3 cm',
  [AJCCStage.STAGE_IB]: 'Larger tumor (> 2 cm and ≤ 4 cm)',
  [AJCCStage.STAGE_II]: 'Larger tumor and/or spread to nearby lymph nodes',
  [AJCCStage.STAGE_IIA]: 'Tumor > 4 cm or N1 involvement',
  [AJCCStage.STAGE_IIB]: 'Tumor > 5 cm or N1 involvement with larger tumor',
  [AJCCStage.STAGE_III]: 'Larger tumor with more extensive lymph node involvement',
  [AJCCStage.STAGE_IIIA]: 'N2 involvement or T4 tumor with N0-1',
  [AJCCStage.STAGE_IIIB]: 'T4 with N2 involvement',
  [AJCCStage.STAGE_IIIC]: 'Any T with N3 involvement',
  [AJCCStage.STAGE_IV]: 'Distant metastasis',
  [AJCCStage.STAGE_IVA]: 'Metastasis to single organ',
  [AJCCStage.STAGE_IVB]: 'Metastasis to multiple organs',
  [AJCCStage.OCCULT]: 'Tumor cannot be found but cancer cells detected',
};

// -----------------------------------------------------------------------------
// PERFORMANCE STATUS
// -----------------------------------------------------------------------------

/**
 * ECOG Performance Status - Eastern Cooperative Oncology Group scale (0-5)
 */
export enum ECOGStatus {
  ECOG_0 = '0',
  ECOG_1 = '1',
  ECOG_2 = '2',
  ECOG_3 = '3',
  ECOG_4 = '4',
  ECOG_5 = '5',
}

export const ECOG_STATUSES = Object.values(ECOGStatus);

export const ECOG_STATUS_DESCRIPTIONS: Record<ECOGStatus, string> = {
  [ECOGStatus.ECOG_0]: 'Fully active, able to carry on all pre-disease performance without restriction',
  [ECOGStatus.ECOG_1]: 'Restricted in physically strenuous activity but ambulatory and able to carry out work of a light or sedentary nature',
  [ECOGStatus.ECOG_2]: 'Ambulatory and capable of all selfcare but unable to carry out any work activities; up and about more than 50% of waking hours',
  [ECOGStatus.ECOG_3]: 'Capable of only limited selfcare; confined to bed or chair more than 50% of waking hours',
  [ECOGStatus.ECOG_4]: 'Completely disabled; cannot carry on any selfcare; totally confined to bed or chair',
  [ECOGStatus.ECOG_5]: 'Dead',
};

/**
 * Karnofsky Performance Scale - 0 to 100 in increments of 10
 */
export enum KarnofskyScore {
  KPS_100 = '100',
  KPS_90 = '90',
  KPS_80 = '80',
  KPS_70 = '70',
  KPS_60 = '60',
  KPS_50 = '50',
  KPS_40 = '40',
  KPS_30 = '30',
  KPS_20 = '20',
  KPS_10 = '10',
  KPS_0 = '0',
}

export const KARNOFSKY_SCORES = Object.values(KarnofskyScore);

export const KARNOFSKY_SCORE_DESCRIPTIONS: Record<KarnofskyScore, string> = {
  [KarnofskyScore.KPS_100]: 'Normal, no complaints, no evidence of disease',
  [KarnofskyScore.KPS_90]: 'Able to carry on normal activity; minor signs or symptoms of disease',
  [KarnofskyScore.KPS_80]: 'Normal activity with effort; some signs or symptoms of disease',
  [KarnofskyScore.KPS_70]: 'Cares for self; unable to carry on normal activity or do active work',
  [KarnofskyScore.KPS_60]: 'Requires occasional assistance but able to care for most needs',
  [KarnofskyScore.KPS_50]: 'Requires considerable assistance and frequent medical care',
  [KarnofskyScore.KPS_40]: 'Disabled; requires special care and assistance',
  [KarnofskyScore.KPS_30]: 'Severely disabled; hospitalization indicated though death not imminent',
  [KarnofskyScore.KPS_20]: 'Very sick; hospitalization necessary; active supportive treatment necessary',
  [KarnofskyScore.KPS_10]: 'Moribund; fatal processes progressing rapidly',
  [KarnofskyScore.KPS_0]: 'Dead',
};

// -----------------------------------------------------------------------------
// CTCAE TOXICITY GRADING
// -----------------------------------------------------------------------------

/**
 * CTCAE Grade - Common Terminology Criteria for Adverse Events (v5.0)
 */
export enum CTCAEGrade {
  GRADE_0 = '0',
  GRADE_1 = '1',
  GRADE_2 = '2',
  GRADE_3 = '3',
  GRADE_4 = '4',
  GRADE_5 = '5',
}

export const CTCAE_GRADES = Object.values(CTCAEGrade);

export const CTCAE_GRADE_DESCRIPTIONS: Record<CTCAEGrade, string> = {
  [CTCAEGrade.GRADE_0]: 'No adverse event or within normal limits',
  [CTCAEGrade.GRADE_1]: 'Mild; asymptomatic or mild symptoms; clinical or diagnostic observations only; intervention not indicated',
  [CTCAEGrade.GRADE_2]: 'Moderate; minimal, local or noninvasive intervention indicated; limiting age-appropriate instrumental ADL',
  [CTCAEGrade.GRADE_3]: 'Severe or medically significant but not immediately life-threatening; hospitalization or prolongation indicated; disabling; limiting self care ADL',
  [CTCAEGrade.GRADE_4]: 'Life-threatening consequences; urgent intervention indicated',
  [CTCAEGrade.GRADE_5]: 'Death related to adverse event',
};

export const CTCAE_GRADE_COLORS: Record<CTCAEGrade, string> = {
  [CTCAEGrade.GRADE_0]: 'success',
  [CTCAEGrade.GRADE_1]: 'info',
  [CTCAEGrade.GRADE_2]: 'warning',
  [CTCAEGrade.GRADE_3]: 'error',
  [CTCAEGrade.GRADE_4]: 'error',
  [CTCAEGrade.GRADE_5]: 'default',
};

/**
 * Common Toxicity Categories for oncology treatments
 */
export enum ToxicityCategory {
  HEMATOLOGIC = 'Hematologic',
  GASTROINTESTINAL = 'Gastrointestinal',
  DERMATOLOGIC = 'Dermatologic',
  NEUROLOGIC = 'Neurologic',
  CARDIAC = 'Cardiac',
  PULMONARY = 'Pulmonary',
  RENAL = 'Renal',
  HEPATIC = 'Hepatic',
  METABOLIC = 'Metabolic',
  MUSCULOSKELETAL = 'Musculoskeletal',
  INFECTION = 'Infection',
  CONSTITUTIONAL = 'Constitutional',
  ALLERGIC = 'Allergic/Immunologic',
  OTHER = 'Other',
}

export const TOXICITY_CATEGORIES = Object.values(ToxicityCategory);

/**
 * Common Adverse Events in Oncology
 */
export enum AdverseEvent {
  // Hematologic
  NEUTROPENIA = 'Neutropenia',
  ANEMIA = 'Anemia',
  THROMBOCYTOPENIA = 'Thrombocytopenia',
  FEBRILE_NEUTROPENIA = 'Febrile Neutropenia',
  LEUKOPENIA = 'Leukopenia',
  // Gastrointestinal
  NAUSEA = 'Nausea',
  VOMITING = 'Vomiting',
  DIARRHEA = 'Diarrhea',
  CONSTIPATION = 'Constipation',
  MUCOSITIS = 'Mucositis',
  ANOREXIA = 'Anorexia',
  // Dermatologic
  ALOPECIA = 'Alopecia',
  RASH = 'Rash',
  HAND_FOOT_SYNDROME = 'Hand-Foot Syndrome',
  NAIL_CHANGES = 'Nail Changes',
  // Neurologic
  PERIPHERAL_NEUROPATHY = 'Peripheral Neuropathy',
  FATIGUE = 'Fatigue',
  COGNITIVE_DYSFUNCTION = 'Cognitive Dysfunction',
  HEADACHE = 'Headache',
  // Cardiac
  CARDIOTOXICITY = 'Cardiotoxicity',
  ARRHYTHMIA = 'Arrhythmia',
  HYPERTENSION = 'Hypertension',
  // Other
  NEPHROTOXICITY = 'Nephrotoxicity',
  HEPATOTOXICITY = 'Hepatotoxicity',
  INFUSION_REACTION = 'Infusion Reaction',
  OTHER = 'Other',
}

export const ADVERSE_EVENTS = Object.values(AdverseEvent);

// -----------------------------------------------------------------------------
// CHEMOTHERAPY
// -----------------------------------------------------------------------------

/**
 * Chemotherapy Drug Categories
 */
export enum ChemoDrugCategory {
  ALKYLATING_AGENT = 'Alkylating Agent',
  ANTIMETABOLITE = 'Antimetabolite',
  ANTHRACYCLINE = 'Anthracycline',
  TAXANE = 'Taxane',
  VINCA_ALKALOID = 'Vinca Alkaloid',
  TOPOISOMERASE_INHIBITOR = 'Topoisomerase Inhibitor',
  PLATINUM_COMPOUND = 'Platinum Compound',
  TARGETED_THERAPY = 'Targeted Therapy',
  IMMUNOTHERAPY = 'Immunotherapy',
  HORMONE_THERAPY = 'Hormone Therapy',
  MONOCLONAL_ANTIBODY = 'Monoclonal Antibody',
  CHECKPOINT_INHIBITOR = 'Checkpoint Inhibitor',
  KINASE_INHIBITOR = 'Kinase Inhibitor',
  PROTEASOME_INHIBITOR = 'Proteasome Inhibitor',
  OTHER = 'Other',
}

export const CHEMO_DRUG_CATEGORIES = Object.values(ChemoDrugCategory);

/**
 * Common Chemotherapy Regimens
 */
export enum ChemoRegimen {
  // Breast Cancer
  AC = 'AC (Doxorubicin/Cyclophosphamide)',
  AC_T = 'AC-T (AC followed by Taxol)',
  TC = 'TC (Docetaxel/Cyclophosphamide)',
  CMF = 'CMF (Cyclophosphamide/Methotrexate/5-FU)',
  TAC = 'TAC (Docetaxel/Doxorubicin/Cyclophosphamide)',
  // Colorectal Cancer
  FOLFOX = 'FOLFOX (5-FU/Leucovorin/Oxaliplatin)',
  FOLFIRI = 'FOLFIRI (5-FU/Leucovorin/Irinotecan)',
  FOLFOXIRI = 'FOLFOXIRI (5-FU/Leucovorin/Oxaliplatin/Irinotecan)',
  CAPOX = 'CAPOX (Capecitabine/Oxaliplatin)',
  // Lung Cancer
  CARBOPLATIN_PACLITAXEL = 'Carboplatin/Paclitaxel',
  CISPLATIN_ETOPOSIDE = 'Cisplatin/Etoposide',
  CISPLATIN_PEMETREXED = 'Cisplatin/Pemetrexed',
  // Lymphoma
  R_CHOP = 'R-CHOP (Rituximab/CHOP)',
  ABVD = 'ABVD (Doxorubicin/Bleomycin/Vinblastine/Dacarbazine)',
  BEACOPP = 'BEACOPP (Bleomycin/Etoposide/Doxorubicin/Cyclophosphamide/Vincristine/Procarbazine/Prednisone)',
  // Other
  GEMCITABINE_CISPLATIN = 'Gemcitabine/Cisplatin',
  BEP = 'BEP (Bleomycin/Etoposide/Cisplatin)',
  CUSTOM = 'Custom Regimen',
}

export const CHEMO_REGIMENS = Object.values(ChemoRegimen);

/**
 * Chemotherapy Cycle Status
 */
export enum ChemoCycleStatus {
  SCHEDULED = 'Scheduled',
  IN_PROGRESS = 'In Progress',
  COMPLETED = 'Completed',
  DELAYED = 'Delayed',
  DOSE_REDUCED = 'Dose Reduced',
  HELD = 'Held',
  CANCELLED = 'Cancelled',
  DISCONTINUED = 'Discontinued',
}

export const CHEMO_CYCLE_STATUSES = Object.values(ChemoCycleStatus);

export const CHEMO_CYCLE_STATUS_COLORS: Record<ChemoCycleStatus, string> = {
  [ChemoCycleStatus.SCHEDULED]: 'info',
  [ChemoCycleStatus.IN_PROGRESS]: 'warning',
  [ChemoCycleStatus.COMPLETED]: 'success',
  [ChemoCycleStatus.DELAYED]: 'warning',
  [ChemoCycleStatus.DOSE_REDUCED]: 'warning',
  [ChemoCycleStatus.HELD]: 'error',
  [ChemoCycleStatus.CANCELLED]: 'default',
  [ChemoCycleStatus.DISCONTINUED]: 'error',
};

/**
 * Dose Modification Reasons
 */
export enum DoseModificationReason {
  NEUTROPENIA = 'Neutropenia',
  THROMBOCYTOPENIA = 'Thrombocytopenia',
  HEPATOTOXICITY = 'Hepatotoxicity',
  NEPHROTOXICITY = 'Nephrotoxicity',
  NEUROPATHY = 'Neuropathy',
  MUCOSITIS = 'Mucositis',
  CARDIAC_TOXICITY = 'Cardiac Toxicity',
  INFECTION = 'Infection',
  PATIENT_REQUEST = 'Patient Request',
  PHYSICIAN_DISCRETION = 'Physician Discretion',
  OTHER = 'Other',
}

export const DOSE_MODIFICATION_REASONS = Object.values(DoseModificationReason);

// -----------------------------------------------------------------------------
// TREATMENT
// -----------------------------------------------------------------------------

/**
 * Treatment Intent - Purpose of oncology treatment
 */
export enum TreatmentIntent {
  CURATIVE = 'Curative',
  ADJUVANT = 'Adjuvant',
  NEOADJUVANT = 'Neoadjuvant',
  PALLIATIVE = 'Palliative',
  MAINTENANCE = 'Maintenance',
  CONSOLIDATION = 'Consolidation',
  SALVAGE = 'Salvage',
  DEFINITIVE = 'Definitive',
}

export const TREATMENT_INTENTS = Object.values(TreatmentIntent);

export const TREATMENT_INTENT_DESCRIPTIONS: Record<TreatmentIntent, string> = {
  [TreatmentIntent.CURATIVE]: 'Treatment aimed at curing the cancer',
  [TreatmentIntent.ADJUVANT]: 'Treatment given after primary treatment to lower recurrence risk',
  [TreatmentIntent.NEOADJUVANT]: 'Treatment given before primary treatment to shrink tumor',
  [TreatmentIntent.PALLIATIVE]: 'Treatment to relieve symptoms and improve quality of life',
  [TreatmentIntent.MAINTENANCE]: 'Treatment to keep cancer from progressing after initial response',
  [TreatmentIntent.CONSOLIDATION]: 'Treatment to further reduce cancer burden after remission',
  [TreatmentIntent.SALVAGE]: 'Treatment after failure of initial therapy',
  [TreatmentIntent.DEFINITIVE]: 'Primary treatment without surgery',
};

/**
 * Treatment Modality - Type of cancer treatment
 */
export enum TreatmentModality {
  CHEMOTHERAPY = 'Chemotherapy',
  RADIATION_THERAPY = 'Radiation Therapy',
  SURGERY = 'Surgery',
  IMMUNOTHERAPY = 'Immunotherapy',
  TARGETED_THERAPY = 'Targeted Therapy',
  HORMONE_THERAPY = 'Hormone Therapy',
  STEM_CELL_TRANSPLANT = 'Stem Cell Transplant',
  CAR_T_CELL = 'CAR T-Cell Therapy',
  RADIOFREQUENCY_ABLATION = 'Radiofrequency Ablation',
  CRYOTHERAPY = 'Cryotherapy',
  PHOTODYNAMIC_THERAPY = 'Photodynamic Therapy',
  WATCHFUL_WAITING = 'Watchful Waiting',
  COMBINATION = 'Combination Therapy',
}

export const TREATMENT_MODALITIES = Object.values(TreatmentModality);

/**
 * Treatment Plan Status
 */
export enum TreatmentPlanStatus {
  DRAFT = 'Draft',
  PENDING_APPROVAL = 'Pending Approval',
  APPROVED = 'Approved',
  ACTIVE = 'Active',
  ON_HOLD = 'On Hold',
  COMPLETED = 'Completed',
  DISCONTINUED = 'Discontinued',
  REVISED = 'Revised',
}

export const TREATMENT_PLAN_STATUSES = Object.values(TreatmentPlanStatus);

// -----------------------------------------------------------------------------
// RESPONSE ASSESSMENT
// -----------------------------------------------------------------------------

/**
 * Response Assessment - RECIST 1.1 Criteria
 */
export enum ResponseAssessment {
  CR = 'CR',   // Complete Response
  PR = 'PR',   // Partial Response
  SD = 'SD',   // Stable Disease
  PD = 'PD',   // Progressive Disease
  NE = 'NE',   // Not Evaluable
}

export const RESPONSE_ASSESSMENTS = Object.values(ResponseAssessment);

export const RESPONSE_ASSESSMENT_DESCRIPTIONS: Record<ResponseAssessment, string> = {
  [ResponseAssessment.CR]: 'Complete Response - Disappearance of all target lesions',
  [ResponseAssessment.PR]: 'Partial Response - ≥30% decrease in sum of target lesion diameters',
  [ResponseAssessment.SD]: 'Stable Disease - Neither sufficient shrinkage nor increase',
  [ResponseAssessment.PD]: 'Progressive Disease - ≥20% increase in sum of target lesion diameters',
  [ResponseAssessment.NE]: 'Not Evaluable - Cannot be assessed',
};

export const RESPONSE_ASSESSMENT_COLORS: Record<ResponseAssessment, string> = {
  [ResponseAssessment.CR]: 'success',
  [ResponseAssessment.PR]: 'info',
  [ResponseAssessment.SD]: 'warning',
  [ResponseAssessment.PD]: 'error',
  [ResponseAssessment.NE]: 'default',
};

// -----------------------------------------------------------------------------
// CLINICAL TRIALS
// -----------------------------------------------------------------------------

/**
 * Clinical Trial Phase
 */
export enum ClinicalTrialPhase {
  PHASE_0 = 'Phase 0',
  PHASE_I = 'Phase I',
  PHASE_I_II = 'Phase I/II',
  PHASE_II = 'Phase II',
  PHASE_II_III = 'Phase II/III',
  PHASE_III = 'Phase III',
  PHASE_IV = 'Phase IV',
}

export const CLINICAL_TRIAL_PHASES = Object.values(ClinicalTrialPhase);

export const CLINICAL_TRIAL_PHASE_DESCRIPTIONS: Record<ClinicalTrialPhase, string> = {
  [ClinicalTrialPhase.PHASE_0]: 'Exploratory study with very limited human exposure',
  [ClinicalTrialPhase.PHASE_I]: 'Safety and dosage study in small group (20-80 patients)',
  [ClinicalTrialPhase.PHASE_I_II]: 'Combined Phase I and II study',
  [ClinicalTrialPhase.PHASE_II]: 'Efficacy and side effects study (100-300 patients)',
  [ClinicalTrialPhase.PHASE_II_III]: 'Combined Phase II and III study',
  [ClinicalTrialPhase.PHASE_III]: 'Large-scale efficacy and monitoring (1000-3000 patients)',
  [ClinicalTrialPhase.PHASE_IV]: 'Post-marketing surveillance study',
};

/**
 * Clinical Trial Enrollment Status
 */
export enum TrialEnrollmentStatus {
  SCREENING = 'Screening',
  ENROLLED = 'Enrolled',
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  WITHDRAWN = 'Withdrawn',
  SCREEN_FAILURE = 'Screen Failure',
  DISCONTINUED = 'Discontinued',
}

export const TRIAL_ENROLLMENT_STATUSES = Object.values(TrialEnrollmentStatus);

// -----------------------------------------------------------------------------
// TUMOR BOARD
// -----------------------------------------------------------------------------

/**
 * Tumor Board Meeting Status
 */
export enum TumorBoardStatus {
  SCHEDULED = 'Scheduled',
  CASE_SUBMITTED = 'Case Submitted',
  UNDER_REVIEW = 'Under Review',
  DISCUSSED = 'Discussed',
  RECOMMENDATIONS_PENDING = 'Recommendations Pending',
  RECOMMENDATIONS_COMPLETE = 'Recommendations Complete',
  CANCELLED = 'Cancelled',
}

export const TUMOR_BOARD_STATUSES = Object.values(TumorBoardStatus);

/**
 * Tumor Board Recommendation Type
 */
export enum TumorBoardRecommendationType {
  PROCEED_AS_PLANNED = 'Proceed as Planned',
  MODIFY_TREATMENT = 'Modify Treatment Plan',
  ADDITIONAL_TESTING = 'Additional Testing Needed',
  CLINICAL_TRIAL = 'Consider Clinical Trial',
  MULTIDISCIPLINARY_APPROACH = 'Multidisciplinary Approach',
  PALLIATIVE_CARE = 'Refer to Palliative Care',
  SECOND_OPINION = 'Seek Second Opinion',
  OBSERVATION = 'Continue Observation',
  OTHER = 'Other',
}

export const TUMOR_BOARD_RECOMMENDATION_TYPES = Object.values(TumorBoardRecommendationType);

// -----------------------------------------------------------------------------
// SURVIVORSHIP
// -----------------------------------------------------------------------------

/**
 * Survivorship Plan Status
 */
export enum SurvivorshipPlanStatus {
  DRAFT = 'Draft',
  ACTIVE = 'Active',
  COMPLETED = 'Completed',
  UPDATED = 'Updated',
  ARCHIVED = 'Archived',
}

export const SURVIVORSHIP_PLAN_STATUSES = Object.values(SurvivorshipPlanStatus);

/**
 * Surveillance Frequency
 */
export enum SurveillanceFrequency {
  MONTHLY = 'Monthly',
  EVERY_3_MONTHS = 'Every 3 Months',
  EVERY_6_MONTHS = 'Every 6 Months',
  ANNUALLY = 'Annually',
  EVERY_2_YEARS = 'Every 2 Years',
  AS_NEEDED = 'As Needed',
}

export const SURVEILLANCE_FREQUENCIES = Object.values(SurveillanceFrequency);

// -----------------------------------------------------------------------------
// BSA CALCULATION FORMULAS
// -----------------------------------------------------------------------------

/**
 * Body Surface Area (BSA) Calculation Formulas
 */
export enum BSAFormula {
  MOSTELLER = 'Mosteller',
  DUBOIS = 'DuBois',
  HAYCOCK = 'Haycock',
  GEHAN_GEORGE = 'Gehan-George',
  BOYD = 'Boyd',
}

export const BSA_FORMULAS = Object.values(BSAFormula);

export const BSA_FORMULA_DESCRIPTIONS: Record<BSAFormula, string> = {
  [BSAFormula.MOSTELLER]: 'Mosteller: BSA = √((height × weight) / 3600) - Most commonly used',
  [BSAFormula.DUBOIS]: 'DuBois: BSA = 0.007184 × height^0.725 × weight^0.425',
  [BSAFormula.HAYCOCK]: 'Haycock: BSA = 0.024265 × height^0.3964 × weight^0.5378 - Recommended for children',
  [BSAFormula.GEHAN_GEORGE]: 'Gehan-George: BSA = 0.0235 × height^0.42246 × weight^0.51456',
  [BSAFormula.BOYD]: 'Boyd: Complex formula accounting for body composition',
};

// -----------------------------------------------------------------------------
// RADIATION THERAPY
// -----------------------------------------------------------------------------

/**
 * Radiation Therapy Technique
 */
export enum RadiationTechnique {
  EBRT_3D = '3D Conformal RT',
  IMRT = 'IMRT (Intensity-Modulated)',
  VMAT = 'VMAT (Volumetric Arc)',
  SBRT = 'SBRT (Stereotactic Body)',
  SRS = 'SRS (Stereotactic Radiosurgery)',
  PROTON = 'Proton Therapy',
  BRACHYTHERAPY = 'Brachytherapy',
  ELECTRON = 'Electron Beam',
  TOTAL_BODY = 'Total Body Irradiation',
}

export const RADIATION_TECHNIQUES = Object.values(RadiationTechnique);

/**
 * Radiation Therapy Status
 */
export enum RadiationStatus {
  PLANNED = 'Planned',
  SIMULATION_COMPLETE = 'Simulation Complete',
  IN_TREATMENT = 'In Treatment',
  COMPLETED = 'Completed',
  ON_HOLD = 'On Hold',
  DISCONTINUED = 'Discontinued',
}

export const RADIATION_STATUSES = Object.values(RadiationStatus);

// -----------------------------------------------------------------------------
// BIOMARKERS
// -----------------------------------------------------------------------------

/**
 * Common Oncology Biomarkers
 */
export enum Biomarker {
  // Breast Cancer
  ER = 'ER (Estrogen Receptor)',
  PR = 'PR (Progesterone Receptor)',
  HER2 = 'HER2',
  KI67 = 'Ki-67',
  BRCA1 = 'BRCA1',
  BRCA2 = 'BRCA2',
  // Lung Cancer
  EGFR = 'EGFR',
  ALK = 'ALK',
  ROS1 = 'ROS1',
  PD_L1 = 'PD-L1',
  KRAS = 'KRAS',
  BRAF = 'BRAF',
  // Colorectal
  MSI = 'MSI (Microsatellite Instability)',
  MMR = 'MMR (Mismatch Repair)',
  NRAS = 'NRAS',
  // General
  TMB = 'TMB (Tumor Mutational Burden)',
  HRD = 'HRD (Homologous Recombination Deficiency)',
  OTHER = 'Other',
}

export const BIOMARKERS = Object.values(Biomarker);

/**
 * Biomarker Result
 */
export enum BiomarkerResult {
  POSITIVE = 'Positive',
  NEGATIVE = 'Negative',
  EQUIVOCAL = 'Equivocal',
  HIGH = 'High',
  LOW = 'Low',
  INTERMEDIATE = 'Intermediate',
  NOT_DETECTED = 'Not Detected',
  PENDING = 'Pending',
}

export const BIOMARKER_RESULTS = Object.values(BiomarkerResult); 